import { Footer } from './Footer';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { getRouteParams, routeNameSelector, routeSelector, marketplaceSelector } from 'store/state/selectors/router';
import {
  addressDocEntrySelector,
  resolvedStrictLocalDocSelector,
  searchDocEntriesSelector,
  userProfileSelector,
} from 'store/state/domainData/selectors';
import { flow } from 'lodash';
import { CompletionType, DealType, IAutocompleteEntry, MarketplaceType } from 'utils/entities';
import { createSelector } from 'reselect';
import { Route } from 'config/routes';
import { config as sectionsConfig } from 'components/footer/config';
import { ITranslate, withLocale, WithLocaleProps } from 'locale';
import { dealTypeSelector, filtersStateSelector } from 'store/state/selectors/search';
import { Seller } from 'components/filters/types';
import { cityFooterSelector } from 'store/state/domainData/selectors/cityFooterByDocId';
import { FooterSection, FooterSectionType, LinkType } from './types';
import { findNeighbourhood } from 'utils/addressFormatter';

const docSelector = createSelector([
  searchDocEntriesSelector,
  addressDocEntrySelector,
  resolvedStrictLocalDocSelector,
  marketplaceSelector,
  routeSelector,
], (searchDocs, addressDoc, localDoc, marketplace, route): IAutocompleteEntry => {
  const isCommercial = marketplace === MarketplaceType.Commercial;

  if (isCommercial && searchDocs && searchDocs.length > 1) return searchDocs[0];

  switch (route.name) {
    case Route.Sold:
    case Route.AddressPage:
      return addressDoc;
    case Route.LocalPage:
    case Route.StreetPage:
      return localDoc;
    default:
      return searchDocs && searchDocs.length === 1 ? searchDocs[0] : null;
  }
});

const higherRelevantDocSelector = createSelector([
  docSelector,
], (doc) => {
  if (doc && doc.type === CompletionType.Address) {
    let relevantHigherLevelDoc = null;
    relevantHigherLevelDoc = doc.relevantDocIds && doc.relevantDocIds.find(item => item.type === CompletionType.Neighbourhood);
    if (!relevantHigherLevelDoc) {
      relevantHigherLevelDoc = doc.relevantDocIds && doc.relevantDocIds.find(item => item.type === CompletionType.City);
    }
    return relevantHigherLevelDoc;
  }
  return null;
});

const breadcrumbsSelector = createSelector([ docSelector ], (docSource) => {
  if (!docSource) return [];

  const city = docSource.hierarchyLevel.find((item) => item.text === docSource.city);
  const neighbourhood = findNeighbourhood(docSource);
  let street = 'street' in docSource ? docSource.hierarchyLevel.find((item) => item.text.startsWith(docSource.street)) : null;

  if (street && 'houseNumber' in docSource && docSource.houseNumber) {
    street = {
      ...street,
      text: `${street.text} ${docSource.houseNumber}`,
    };
  }

  const sortedBreadcrumbs = [ city, neighbourhood, street ].filter(Boolean);

  return docSource.hierarchyLevel.length > sortedBreadcrumbs.length ? docSource.hierarchyLevel : sortedBreadcrumbs;
});

const cityFooterSectionSelector = createSelector([
  cityFooterSelector,
  routeNameSelector,
  getRouteParams,
], (data, routeName, { term }): FooterSection => {
  const oneTerm = term && term.length === 1;
  const isLocalPage = routeName === Route.LocalPage;
  const isSearch = routeName === Route.Search;
  const allowedPage = isLocalPage || isSearch;

  if (data && oneTerm && allowedPage) {
    return {
      type: FooterSectionType.CityFooter,
      links: data.rows.map(({ link, copy, docId }) => ({
        id: docId,
        text: copy,
        newTab: false,
        type: LinkType.ALink,
        href: link,
      })),
    };
  }
  return null;
});

const addressCompletionSelector = flow(
  docSelector, (doc) => {
    if (doc) {
      switch (doc.type) {
        case CompletionType.Neighbourhood:
          return doc.neighbourhood;
        case CompletionType.City:
          return doc.city;
        default:
          return doc.name;
      }
    }
    return '';
  }
);

const sectionsSelector = createSelector([
  routeSelector,
  addressCompletionSelector,
  docSelector,
  higherRelevantDocSelector,
  filtersStateSelector,
  marketplaceSelector,
  () => sectionsConfig,
  (_: State, t: ITranslate) => t,
], (route, accurateAddress, doc, higherRelevantDoc, filtersState, marketplace, sectionConfig, t) => {
  const isHome = route.name === Route.Home;
  if (doc && doc.docId || isHome) {
    const { docId, type: completionType } = doc;
    const isProjectsView = filtersState && filtersState.seller.length === 1 && filtersState.seller[0] === Seller.Developer;
    const dealType = route.params.dealType || DealType.Rent;
    const sectionsFn = sectionConfig[dealType];

    if (completionType === CompletionType.Address && higherRelevantDoc) {
      const { docId: higherDocId, type: higherDocCompletionType, text: higherDocText } = higherRelevantDoc;
      return sectionsFn({ t, docId: higherDocId, completionType: higherDocCompletionType, accurateAddress: higherDocText, isProjectsView, marketplace });
    }

    return sectionsFn({ t, docId, completionType, accurateAddress, isProjectsView, marketplace });
  }
  return [];
});

const nearbyNeighbourhoodsSelector = flow(
  docSelector,
  (doc) => doc && doc.type === CompletionType.Neighbourhood && doc.nearbyNeighbourhoods && doc.nearbyNeighbourhoods.length
    ? doc.nearbyNeighbourhoods
    : null
);

const mapStateToProps = (state: State, props: WithLocaleProps) => ({
  route: routeSelector(state),
  breadcrumbs: breadcrumbsSelector(state),
  nearbyNeighbourhoods: nearbyNeighbourhoodsSelector(state),
  sections: sectionsSelector(state, props.t),
  user: userProfileSelector(state),
  marketplace: marketplaceSelector(state),
  cityFooter: cityFooterSectionSelector(state),
  dealTypeFromBulletin: dealTypeSelector(state),
});

export default withLocale(connect(mapStateToProps)(Footer));
